import React, { useEffect } from 'react';
import { CircularProgress, InputAdornment, TextField } from '@mui/material';
import { InputValueType, SetValues } from '../../../hooks/useForm/useForm';
import HelperText from '../../HelperText/HelperText';
import _ from 'lodash';
import axios from '../../../libraries/axios/axios';
import { useMutation } from '@tanstack/react-query';
import { HttpError, HttpResponse } from '../../../types/Api';
import { getObjectValue } from '../../List/List.helpers';

export type AutofillValue = {
  name: string;
  key: string;
};

export type AutofillConfig = {
  apiUrl: string;
  apiValueKey: string;
  apiValueKeys?: string[];
  fillValues: AutofillValue[];
};

export type TextInputProps = {
  label?: string;
  placeholder?: string;
  name?: string;
  value?: InputValueType;
  errors?: string[];
  onChange?: (name: string, value: string) => void;
  onBlur?: Function;
  autoFocus?: boolean;
  className?: string;
  isReadOnly?: boolean;
  autofillConfig?: AutofillConfig;
  upperCase?: boolean;
  getValue?: (name: string) => InputValueType;
};

export const TextInput = ({
  label,
  placeholder,
  name,
  value,
  errors,
  onChange,
  onBlur,
  autoFocus,
  className,
  isReadOnly,
  autofillConfig,
  upperCase,
  getValue,
}: TextInputProps) => {
  const { mutate: getAutofill, isPending } = useMutation<
    HttpResponse<SetValues>,
    HttpError
  >({
    mutationFn: () => {
      const params = {
        [autofillConfig?.apiValueKey ?? '']: value,
      };

      if (autofillConfig?.apiValueKeys) {
        autofillConfig?.apiValueKeys?.forEach(
          (apiValueKey) => (params[apiValueKey] = getValue?.(apiValueKey)),
        );
      }

      return axios.get(autofillConfig?.apiUrl ?? '', {
        params: params,
      });
    },
    onSuccess: (response) => {
      if (!response?.data) {
        return;
      }

      for (const autofillValue of autofillConfig?.fillValues ?? []) {
        onChange?.(
          autofillValue.name,
          getObjectValue(response.data, autofillValue.key),
        );
      }
    },
  });

  const handleChange = (e: any) => {
    if (upperCase) {
      onChange?.(name ?? '', e.target.value?.toUpperCase());
      return;
    }

    onChange?.(name ?? '', e.target.value);
  };

  const debouncedHandleValueChange = _.debounce(() => getAutofill(), 300);

  useEffect(() => {
    if (autofillConfig && value) {
      debouncedHandleValueChange();
    }
  }, [autofillConfig, value]);

  return (
    <TextField
      label={label}
      placeholder={placeholder}
      name={name}
      value={value}
      onChange={handleChange}
      onBlur={() => onBlur?.(name)}
      helperText={errors && errors.length > 0 && <HelperText errors={errors} />}
      error={errors && errors.length > 0}
      autoFocus={autoFocus}
      className={className}
      disabled={isReadOnly}
      InputProps={{
        endAdornment: isPending ? (
          <InputAdornment position="start">
            <CircularProgress size={14} />
          </InputAdornment>
        ) : null,
      }}
    />
  );
};

export default TextInput;
